import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { CloseIcon, SearchIcon, TrashIcon } from './Icons';
import { COUNTRIES, countryForNumber } from './countryCodes';
import { formatMoney, readJson } from './intlOrderUtils';

const PRODUCT_SEARCH_DEBOUNCE_MS = 300;
// Same reason as InternationalOrders.js's draft key: switching sidebar tabs
// unmounts the page, which would otherwise throw away a half-typed order.
const CREATE_DRAFT_KEY = 'intlOrderCreateDraft';

export const EMPTY_CUSTOMER = {
  customer_name: '', customer_number: '', email: '', country: '', country_code: '',
  state: '', city: '', address_line1: '', address_line2: '', postal_code: '', customer_notes: '',
};

const readDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CREATE_DRAFT_KEY) || 'null');
  } catch {
    return null;
  }
};
const writeDraft = (draft) => {
  try {
    if (draft) sessionStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify(draft));
    else sessionStorage.removeItem(CREATE_DRAFT_KEY);
  } catch {
    // sessionStorage unavailable; the draft just won't survive a tab switch.
  }
};
export const hasCreateDraft = () => Boolean(readDraft());

export const customerFromOrder = (order) =>
  Object.fromEntries(Object.keys(EMPTY_CUSTOMER).map((key) => [key, order[key] || '']));

// Customer + shipping address fields, shared by the create form and the
// detail page's "Edit customer/address" dialog.
export function CustomerFields({ value, onChange, lockNumber = false }) {
  const set = (field) => (event) => onChange({ ...value, [field]: event.target.value });
  const setCountry = (event) => {
    const country = COUNTRIES.find((c) => c.iso2 === event.target.value);
    onChange({ ...value, country_code: country?.iso2 || '', country: country?.name || '' });
  };
  return (
    <div className="intl-form-grid">
      <label className="login-field">
        <span>Customer name *</span>
        <input type="text" value={value.customer_name} onChange={set('customer_name')} required />
      </label>
      <label className="login-field">
        <span>WhatsApp number *</span>
        <input
          type="tel"
          value={value.customer_number}
          onChange={set('customer_number')}
          placeholder="+1 415 555 0100"
          readOnly={lockNumber}
          title={lockNumber ? 'From the WhatsApp conversation' : undefined}
          required
        />
      </label>
      <label className="login-field">
        <span>Email</span>
        <input type="email" value={value.email} onChange={set('email')} />
      </label>
      <label className="login-field">
        <span>Country *</span>
        <select value={value.country_code} onChange={setCountry}>
          <option value="">Select country</option>
          {COUNTRIES.map((country) => (
            <option key={country.iso2} value={country.iso2}>
              {country.flag} {country.name}
            </option>
          ))}
        </select>
      </label>
      <label className="login-field intl-form-wide">
        <span>Address *</span>
        <input type="text" value={value.address_line1} onChange={set('address_line1')} placeholder="Street address, building" />
      </label>
      <label className="login-field intl-form-wide">
        <span>Address line 2</span>
        <input type="text" value={value.address_line2} onChange={set('address_line2')} placeholder="Apartment, suite, landmark" />
      </label>
      <label className="login-field">
        <span>City</span>
        <input type="text" value={value.city} onChange={set('city')} />
      </label>
      <label className="login-field">
        <span>State / Province</span>
        <input type="text" value={value.state} onChange={set('state')} />
      </label>
      <label className="login-field">
        <span>Postal / ZIP code *</span>
        <input type="text" value={value.postal_code} onChange={set('postal_code')} />
      </label>
      <label className="login-field intl-form-wide">
        <span>Customer notes</span>
        <textarea rows={2} value={value.customer_notes} onChange={set('customer_notes')} placeholder="Anything the customer asked for" />
      </label>
    </div>
  );
}

// Converts server item rows into the picker's line shape.
export const linesFromItems = (items) =>
  (items || []).map((item) => ({
    key: `item-${item.id}`,
    id: item.id,
    erpProductId: item.erp_product_id,
    name: item.name,
    sku: item.sku,
    image_url: item.image_url,
    unitPricePaise: item.unit_price_paise,
    qty: item.qty,
    custom: isCustomItem(item),
  }));

// An extra item that isn't in the ERP catalog (see parseCustomItem on the
// server): stored with an erp_product_id starting with "custom:".
export const isCustomItem = (item) => String(item?.erp_product_id || '').startsWith('custom:');

// What the server needs: existing lines by id (their price snapshot is
// kept), new lines by ERP product id. ERP prices are never sent; only an
// extra (non-ERP) item sends its own name and price.
export const itemsPayload = (lines) =>
  lines.map((line) => {
    const qty = Number(line.qty);
    if (line.custom) {
      const custom = { name: line.name, sku: line.sku || '', unit_price: line.unitPricePaise / 100, qty };
      return line.id ? { id: line.id, ...custom } : { custom: true, ...custom };
    }
    return line.id ? { id: line.id, qty } : { erpProductId: line.erpProductId, qty };
  });

// Search the ERP catalog (debounced, 20 results max, nothing preloaded) and
// build the order's product lines. Totals shown here are an estimate for
// the agent; the server recalculates them from ERP prices on save.
export function ProductPicker({ lines, onChange, currency = 'INR' }) {
  const { authFetch } = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError('');
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const body = await readJson(
          await authFetch(`/api/international-orders/products?search=${encodeURIComponent(term)}`),
          'Unable to load product information. Please try again.'
        );
        setResults(body.products || []);
        setSearchError('');
      } catch (err) {
        setResults([]);
        setSearchError(err.message);
      } finally {
        setSearching(false);
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, authFetch]);

  const inOrder = new Set(lines.map((line) => line.erpProductId));

  const addProduct = (product) => {
    onChange([
      ...lines,
      {
        key: `new-${product.id}`,
        erpProductId: product.id,
        name: product.name,
        sku: product.sku,
        image_url: product.image_url,
        unitPricePaise: Math.round(Number(product.selling_price) * 100),
        qty: 1,
      },
    ]);
    setSearch('');
    setResults([]);
  };

  // Extra item that isn't in the ERP: typed name and price.
  const [extra, setExtra] = useState(null);
  const extraPrice = extra ? Math.round(Number(extra.price) * 100) : 0;
  const extraValid = extra && extra.name.trim() && extraPrice > 0 && Number(extra.qty) > 0;
  const addExtra = () => {
    if (!extraValid) return;
    onChange([
      ...lines,
      {
        key: `custom-${Date.now()}`,
        custom: true,
        name: extra.name.trim(),
        sku: extra.sku.trim(),
        image_url: null,
        unitPricePaise: extraPrice,
        qty: extra.qty,
      },
    ]);
    setExtra(null);
  };
  const setPrice = (key, rupees) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, priceInput: rupees, unitPricePaise: Math.round(Number(rupees) * 100) || 0 } : line)));

  const setQty = (key, qty) => onChange(lines.map((line) => (line.key === key ? { ...line, qty } : line)));
  const remove = (key) => onChange(lines.filter((line) => line.key !== key));
  const subtotal = lines.reduce((sum, line) => sum + Math.round(line.unitPricePaise * (Number(line.qty) || 0)), 0);

  return (
    <div className="intl-picker">
      <div className="search-input-wrap intl-picker-search">
        <SearchIcon className="search-input-icon" width={15} height={15} />
        <input
          type="text"
          className="search-input"
          placeholder="Search ERP products by name or SKU"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search products"
        />
      </div>
      {searching && <div className="intl-muted">Searching…</div>}
      {searchError && <div className="call-feedback call-feedback-error">{searchError}</div>}
      {results.length > 0 && (
        <ul className="intl-picker-results">
          {results.map((product) => {
            const priced = product.selling_price !== null && Number(product.selling_price) > 0;
            const added = inOrder.has(product.id);
            return (
              <li key={product.id}>
                <button type="button" disabled={!priced || added} onClick={() => addProduct(product)}>
                  {product.image_url ? <img src={product.image_url} alt="" className="intl-thumb" /> : <span className="intl-thumb" />}
                  <span className="intl-picker-name">
                    {product.name}
                    {product.sku && <span className="intl-muted"> · {product.sku}</span>}
                  </span>
                  <span className="intl-picker-price">
                    {added ? 'Added' : priced ? formatMoney(Math.round(Number(product.selling_price) * 100), currency) : 'Price unavailable in ERP'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {search.trim().length >= 2 && !searching && !searchError && results.length === 0 && (
        <div className="intl-muted">No products match "{search.trim()}".</div>
      )}

      {extra ? (
        <div className="intl-extra-item">
          <div className="intl-shipto-title">Extra item (not in ERP)</div>
          <div className="intl-extra-item-grid">
            <label className="login-field">
              <span>Item name *</span>
              <input type="text" value={extra.name} onChange={(event) => setExtra({ ...extra, name: event.target.value })} autoFocus />
            </label>
            <label className="login-field">
              <span>SKU</span>
              <input type="text" value={extra.sku} onChange={(event) => setExtra({ ...extra, sku: event.target.value })} />
            </label>
            <label className="login-field">
              <span>Unit price ({currency}) *</span>
              <input type="number" min="0" step="0.01" value={extra.price} onChange={(event) => setExtra({ ...extra, price: event.target.value })} />
            </label>
            <label className="login-field">
              <span>Qty *</span>
              <input type="number" min="0.001" step="any" value={extra.qty} onChange={(event) => setExtra({ ...extra, qty: event.target.value })} />
            </label>
          </div>
          <div className="cod-actions">
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setExtra(null)}>Cancel</button>
            <button type="button" className="btn btn-primary btn-small" disabled={!extraValid} onClick={addExtra}>Add item</button>
          </div>
        </div>
      ) : (
        <button type="button" className="link-btn intl-extra-item-toggle" onClick={() => setExtra({ name: '', sku: '', price: '', qty: 1 })}>
          + Add an item that isn't in the ERP
        </button>
      )}

      {lines.length > 0 ? (
        <table className="intl-lines">
          <thead>
            <tr>
              <th>Product</th>
              <th className="intl-num">Qty</th>
              <th className="intl-num">Unit price</th>
              <th className="intl-num">Total</th>
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.key}>
                <td>
                  <div className="intl-line-product">
                    {line.image_url ? <img src={line.image_url} alt="" className="intl-thumb" /> : <span className="intl-thumb" />}
                    <span>
                      {line.name}
                      {line.custom && <span className="intl-pill intl-pill-neutral intl-extra-pill">Extra item</span>}
                      {line.sku && <div className="intl-muted">{line.sku}</div>}
                    </span>
                  </div>
                </td>
                <td className="intl-num">
                  <input
                    type="number"
                    min="0.001"
                    step="any"
                    value={line.qty}
                    onChange={(event) => setQty(line.key, event.target.value)}
                    className="intl-qty"
                    aria-label={`Quantity of ${line.name}`}
                  />
                </td>
                <td className="intl-num">
                  {line.custom ? (
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={line.priceInput ?? String(line.unitPricePaise / 100)}
                      onChange={(event) => setPrice(line.key, event.target.value)}
                      className="intl-qty"
                      aria-label={`Unit price of ${line.name}`}
                    />
                  ) : formatMoney(line.unitPricePaise, currency)}
                </td>
                <td className="intl-num">{formatMoney(Math.round(line.unitPricePaise * (Number(line.qty) || 0)), currency)}</td>
                <td>
                  <button type="button" className="icon-btn" onClick={() => remove(line.key)} aria-label={`Remove ${line.name}`}>
                    <TrashIcon width={14} height={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Product subtotal</td>
              <td className="intl-num">{formatMoney(subtotal, currency)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      ) : (
        <div className="intl-empty">No products yet. Search above to add one.</div>
      )}
    </div>
  );
}

// "New international order". When opened from a WhatsApp chat, the number
// comes from that conversation and the name, email, city/state and the
// customer's last international address are filled in from what the CRM
// already has.
export function CreateIntlOrderModal({ prefillNumber, onClose, onCreated }) {
  const { authFetch } = useAuth();
  const saved = prefillNumber ? null : readDraft();
  const [customer, setCustomer] = useState(saved?.customer || { ...EMPTY_CUSTOMER, customer_number: prefillNumber || '' });
  const [lines, setLines] = useState(saved?.lines || []);
  const [prefillNote, setPrefillNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null); // null | 'draft' | 'submit'

  useEffect(() => {
    if (!prefillNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const body = await readJson(
          await authFetch(`/api/international-orders/customer-prefill/${encodeURIComponent(prefillNumber)}`),
          'Could not load customer details.'
        );
        if (cancelled) return;
        const profile = body.profile || {};
        const last = body.lastAddress || {};
        const guess = countryForNumber(body.customer_number);
        const guessIso = guess && COUNTRIES.find((c) => c.name === guess.name)?.iso2;
        setCustomer((prev) => ({
          ...prev,
          customer_number: body.customer_number || prev.customer_number,
          customer_name: prev.customer_name || last.customer_name || profile.name || '',
          email: prev.email || last.email || profile.email || '',
          country: prev.country || last.country || (guessIso ? guess.name : ''),
          country_code: prev.country_code || last.country_code || guessIso || '',
          state: prev.state || last.state || profile.state || '',
          city: prev.city || last.city || profile.city || '',
          address_line1: prev.address_line1 || last.address_line1 || '',
          address_line2: prev.address_line2 || last.address_line2 || '',
          postal_code: prev.postal_code || last.postal_code || '',
        }));
        setPrefillNote(
          body.lastAddress?.address_line1
            ? "Filled in from this customer's last international order. Check the address is still current."
            : profile.name
            ? 'Filled in from the customer profile.'
            : ''
        );
      } catch {
        // Prefill is a convenience; the agent can still type everything.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefillNumber, authFetch]);

  useEffect(() => {
    if (!prefillNumber) writeDraft({ customer, lines });
  }, [customer, lines, prefillNumber]);

  const close = () => {
    writeDraft(null);
    onClose();
  };

  const save = async (submit) => {
    setSaving(submit ? 'submit' : 'draft');
    setError('');
    try {
      const order = await readJson(
        await authFetch('/api/international-orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer, items: itemsPayload(lines), submit }),
        }),
        'Could not save this order.'
      );
      writeDraft(null);
      onCreated(order, submit);
    } catch (err) {
      setError(err.message);
      setSaving(null);
    }
  };

  const canSubmit = useMemo(() => lines.length > 0 && lines.every((line) => Number(line.qty) > 0), [lines]);

  return (
    <div className="modal-overlay modal-overlay-center">
      <div className="modal-panel intl-modal-wide">
        <div className="modal-header">
          <div>
            <h2>New International Order</h2>
            <p className="modal-subtitle">Prices come from the ERP. Stock is not reserved or deducted.</p>
          </div>
          <button className="modal-close" onClick={close} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={(event) => { event.preventDefault(); save(true); }}>
          <h3 className="intl-section-title">Customer</h3>
          {prefillNote && <div className="intl-muted intl-prefill-note">{prefillNote}</div>}
          <CustomerFields value={customer} onChange={setCustomer} lockNumber={Boolean(prefillNumber)} />

          <h3 className="intl-section-title">Products</h3>
          <ProductPicker lines={lines} onChange={setLines} />
          <p className="intl-muted">Courier charge: added by logistics after they verify the address.</p>

          {error && <div className="login-error">{error}</div>}
          <div className="follow-up-add-actions">
            <button type="button" className="btn btn-secondary" onClick={close} disabled={Boolean(saving)}>
              Cancel
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => save(false)} disabled={Boolean(saving)}>
              {saving === 'draft' ? 'Saving…' : 'Save as draft'}
            </button>
            <button type="submit" className="btn btn-primary" disabled={Boolean(saving) || !canSubmit}>
              {saving === 'submit' ? 'Saving…' : 'Save & send to logistics'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
